// Sistema di notifiche toast tematizzato per iotilize.me
//
// Wrapper su react-hot-toast con stile coerente al design system (teal/cream/neutral).
// API:
//   showToast.success('Salvato', 'Dettagli opzionali')
//   showToast.error('Eliminazione fallita', err.message)
//   showToast.info('Messaggio neutro')
//   showToast.warning('Attenzione')
//   showToast.loading('Caricamento…')          → ritorna un id per dismiss manuale
//   showToast.dismiss(id?)                      → chiude un toast (o tutti se id omesso)
//   showToast.promise(p, { loading, success, error })
//
// Il componente <ToastHost /> va montato una sola volta nella root (gatsby-browser.js).

import React from 'react';
import toast, { Toaster } from 'react-hot-toast';

// ── Design tokens (allineati con global.css e AuthLayout) ─────────────────────
const TOKENS = {
  cream:     '#fafaf7',
  surface:   '#ffffff',
  border:    '#e8e8e0',
  text:      '#1a1a14',
  textMuted: '#6b6b60',
  teal:      '#2D9B9B',
  tealDark:  '#1F7373',
  success:   '#2D9B9B',
  error:     '#DC2626',
  warning:   '#F59E0B',
  info:      '#3B82F6',
};

// ── Icone SVG inline (stile coerente con components/index.js Icons) ───────────
const Icons = {
  success: (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="10" cy="10" r="9" fill="currentColor" opacity="0.12" />
      <path d="M5.5 10.5l3 3 6-6.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  error: (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="10" cy="10" r="9" fill="currentColor" opacity="0.12" />
      <path d="M6.5 6.5l7 7M13.5 6.5l-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
  warning: (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M10 2.5L1.5 17h17L10 2.5z" fill="currentColor" opacity="0.12" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M10 8v4M10 14.5v0.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  ),
  info: (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="10" cy="10" r="9" fill="currentColor" opacity="0.12" />
      <path d="M10 9v5M10 6v0.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  ),
  spinner: (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true" style={{ animation: 'iotilize-toast-spin 0.8s linear infinite' }}>
      <circle cx="10" cy="10" r="7.5" stroke="currentColor" strokeWidth="2" opacity="0.2" />
      <path d="M17.5 10a7.5 7.5 0 00-7.5-7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
  close: (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  ),
};

// ── Card visiva del toast ─────────────────────────────────────────────────────
const ToastCard = ({ t, variant, title, message, accentColor }) => (
  <div
    role="status"
    style={{
      pointerEvents: 'auto',
      display: 'flex',
      alignItems: 'flex-start',
      gap: 12,
      minWidth: 280,
      maxWidth: 420,
      padding: '12px 14px',
      background: TOKENS.surface,
      border: `1px solid ${TOKENS.border}`,
      borderLeft: `3px solid ${accentColor}`,
      borderRadius: 10,
      boxShadow: '0 8px 28px rgba(26, 26, 20, 0.10), 0 2px 6px rgba(26, 26, 20, 0.05)',
      fontFamily: 'inherit',
      opacity: t.visible ? 1 : 0,
      transform: t.visible ? 'translateY(0) scale(1)' : 'translateY(8px) scale(0.96)',
      transition: 'opacity 0.18s ease, transform 0.22s cubic-bezier(0.2, 0.9, 0.3, 1.2)',
    }}
  >
    <div style={{ flexShrink: 0, color: accentColor, marginTop: 1 }}>
      {Icons[variant]}
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: TOKENS.text, lineHeight: 1.35 }}>
        {title}
      </div>
      {message && (
        <div style={{ fontSize: 12, fontWeight: 400, color: TOKENS.textMuted, marginTop: 3, lineHeight: 1.45, wordBreak: 'break-word' }}>
          {message}
        </div>
      )}
    </div>
    {variant !== 'loading' && (
      <button
        type="button"
        onClick={() => toast.dismiss(t.id)}
        aria-label="Chiudi"
        style={{
          flexShrink: 0,
          padding: 4,
          marginTop: -2,
          marginRight: -4,
          background: 'transparent',
          border: 'none',
          borderRadius: 4,
          color: TOKENS.textMuted,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background 0.15s, color 0.15s',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = TOKENS.cream; e.currentTarget.style.color = TOKENS.text; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent';   e.currentTarget.style.color = TOKENS.textMuted; }}
      >
        {Icons.close}
      </button>
    )}
  </div>
);

// ── Factory per la chiamata react-hot-toast ───────────────────────────────────
const makeToast = (variant, accentColor, defaultDuration) =>
  (title, message, opts = {}) => toast.custom(
    (t) => <ToastCard t={t} variant={variant} title={title} message={message} accentColor={accentColor} />,
    { duration: opts.duration ?? defaultDuration, id: opts.id, position: opts.position }
  );

// ── API pubblica ──────────────────────────────────────────────────────────────
export const showToast = {
  success: makeToast('success', TOKENS.success, 3500),
  error:   makeToast('error',   TOKENS.error,   6000),
  warning: makeToast('warning', TOKENS.warning, 5000),
  info:    makeToast('info',    TOKENS.info,    4000),
  loading: (title, message) => toast.custom(
    (t) => <ToastCard t={t} variant="loading" title={title} message={message} accentColor={TOKENS.teal} />,
    { duration: Infinity }
  ),
  dismiss: (id) => toast.dismiss(id),

  // Wrapper convenience per Promise → loading + success/error automatici
  promise: (promise, { loading, success, error } = {}) => {
    const id = showToast.loading(loading || 'Operazione in corso…');
    return promise.then(
      (value) => {
        toast.dismiss(id);
        showToast.success(typeof success === 'function' ? success(value) : (success || 'Operazione completata'));
        return value;
      },
      (err) => {
        toast.dismiss(id);
        showToast.error(
          typeof error === 'function' ? error(err) : (error || 'Operazione fallita'),
          err?.message,
        );
        throw err;
      }
    );
  },
};

// ── Host (montato una sola volta in gatsby-browser.js) ────────────────────────
//
// Usiamo Toaster con renderer personalizzato per Icons.spinner (keyframes inline).
export const ToastHost = () => (
  <>
    <style>{`@keyframes iotilize-toast-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    <Toaster
      position="bottom-right"
      gutter={10}
      containerStyle={{ bottom: 24, right: 24 }}
      toastOptions={{ duration: 4000 }}
    />
  </>
);


export default showToast;
